import { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { Palette } from '@/themes/themeOverrides/Palette';
import {
    ArrayField,
    SingleFieldList,
    FunctionField,
    TextField,
    useResourceContext,
    useDataProvider,
    RecordContextProvider,
    usePermissions,
    useTranslate,
    useRecordContext,
} from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import getLoadingState from '@/helpers/ui/getLoadingState.jsx';
import { formatTimeInterval } from '@/helpers/date/formatTimeInterval.js';
import { formatWeight } from '@/helpers/format/formatNumber.js';
import { TransactionDetailsProductCard } from '@/components/transaction/TransactionDetailsProductCard';
import TransactionOperatorStatusField from '@/components/transaction/TransactionOperatorStatusField.jsx';
import TransactionStatusField from '@/components/transaction/TransactionStatusField.jsx';

const StyledTransactionDetailsContentTopBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    backgroundColor: Palette.gray.gray100,
    borderRadius: '16px',
    letterSpacing: '-0.14px',
    margin: '24px 32px 14px 32px',
    gap: '24px',
    padding: '24px 40px',
    [theme.breakpoints.down('md')]: {
        gap: '32px',
        padding: '24px',
        margin: '24px 40px 0',
    },
    '& > div': {
        marginRight: '24px',
    },
    '& .MuiTypography-root.MuiTypography-caption': {
        color: Palette.gray.gray600,
        fontSize: '11px !important',
        lineHeight: '16px !important',
        letterSpacing: '-0.11px',
        marginBottom: '3px',
    },
    '& .MuiTypography-h6': {
        fontWeight: '600',
    },
}));

export const TransactionDetailsContent = () => {
    const record = useRecordContext();
    const requestedResource = useResourceContext();
    const dataProvider = useDataProvider();
    const { permissions } = usePermissions();
    const translate = useTranslate();
    const resource = ['transaction', 'test-transaction'].includes(requestedResource) ? requestedResource : 'transaction';

    const elementId = record.id;
    const {
        data: transaction,
        isPending,
        error,
    } = useQuery({
        queryKey: ['transaction', elementId, resource],
        queryFn: () => dataProvider.getOne(resource, { id: elementId }),
        enabled: !!elementId,
        select: response => response.data,
        onError: err => {
            console.error('Error fetching transaction:', err);
        },
    });

    const loadingState = getLoadingState({ isLoading: isPending, error, data: transaction, translate });

    const lastScrolledIdRef = useRef(null);

    useEffect(() => {
        if (!transaction || !elementId) return;
        if (lastScrolledIdRef.current === elementId) return;

        const el = document.getElementById(elementId);
        if (el) {
            const rect = el.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetTop = rect.top + scrollTop;
            window.scrollTo({ top: targetTop, behavior: 'smooth' });
            lastScrolledIdRef.current = elementId;
        }
    }, [elementId, transaction]);

    if (loadingState) return loadingState;

    return (
        <RecordContextProvider value={transaction}>
            <Box className='transaction-details-content'>
                <StyledTransactionDetailsContentTopBar id={elementId} className='transaction-details-content-top-bar'>
                    <Box>
                        <Typography variant='caption'>{translate('resources.transaction.labels.transactionTimeCaption')}</Typography>
                        <FunctionField source='duration' variant='h6' render={record => formatTimeInterval(record.duration)} />
                    </Box>
                    <Box>
                        <Typography variant='caption'>{translate('resources.transaction.labels.totalWeightCaption')}</Typography>
                        <FunctionField source='totalWeight' variant='h6' render={record => formatWeight(record.totalWeight)} />
                    </Box>
                    <Box>
                        <Typography variant='caption'>{translate('resources.transaction.labels.receiptCode')}</Typography>
                        <TextField variant='h6' source='payout.id' />
                    </Box>
                    <Box>
                        <Typography variant='caption'>{translate('common.deviceSerialNumberShort')}</Typography>
                        <TextField variant='h6' source='device.serialNumber' />
                    </Box>
                    <Box>
                        <Typography variant='caption'>{translate('resources.transaction.labels.transactionIdCaption')}</Typography>
                        <FunctionField source='id' variant='h6' render={record => record.id} />
                    </Box>
                    <Box>
                        <Typography variant='caption'>{translate('common.bag')}</Typography>
                        <FunctionField
                            source='bag.seal'
                            variant='h6'
                            render={record => (record.bag ? (record.bag.seal ? record.bag.seal : record.bag.id) : null)}
                        />
                    </Box>
                    {permissions?.isAdmin && (
                        <>
                            <Box>
                                <Typography variant='caption'>{translate('common.status')}</Typography>
                                <TransactionStatusField />
                            </Box>
                            <Box>
                                <Typography variant='caption'>{translate('resources.transaction.labels.operatorStatusCaption')}</Typography>
                                <TransactionOperatorStatusField />
                            </Box>
                        </>
                    )}
                </StyledTransactionDetailsContentTopBar>

                <ArrayField source='transactionContainers'>
                    <SingleFieldList linkType={false} sx={{ flexDirection: 'column' }}>
                        <TransactionDetailsProductCard />
                    </SingleFieldList>
                </ArrayField>
            </Box>
        </RecordContextProvider>
    );
};
